import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="AutomateSEO.ai" className="h-11 w-auto" />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Refund Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 7, 2025</p>
        
        <div className="prose prose-lg max-w-none space-y-8">
          <section className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">3-Day Free Trial & Refund Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>AutomateSEO.ai offers a 3-day free trial period</strong> for all new users. During this trial, you can explore all features of your chosen plan at no cost.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-red-600">Important:</strong> Refunds are only available within 3 days from the date of your first payment. After this period, no refunds will be processed.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Free Trial Period</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>1.1 Trial Duration:</strong> All new users receive a 3-day free trial upon registration. No credit card is required to start the trial.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>1.2 Trial Features:</strong> During the trial period, you have full access to all features of the Starter Plan, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>SEO automation tools</li>
              <li>Social media content generation</li>
              <li>Blog post creation</li>
              <li>AI-powered media generation</li>
              <li>Weekly reports</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>1.3 Cancel Anytime:</strong> You can cancel your trial at any time during the 3-day period without any charges or obligations.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>1.4 Trial to Paid:</strong> After the 3-day trial ends, you must subscribe to a paid plan to continue using the service. Your trial will automatically expire if no payment is made.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">2. Refund Eligibility</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>2.1 3-Day Refund Window:</strong> Refunds are ONLY available within 3 days from the date of your first payment/subscription activation. This period starts from the moment your payment is processed.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>2.2 Calculation:</strong> The 3-day period is calculated as:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Day 1: Date of payment</li>
              <li>Day 2: First day after payment</li>
              <li>Day 3: Second day after payment</li>
              <li>After Day 3: No refunds will be processed</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>2.3 Eligible Reasons:</strong> Refund requests within the 3-day window will be considered for the following reasons:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Technical issues preventing service use</li>
              <li>Service not meeting expectations</li>
              <li>Accidental subscription</li>
              <li>Duplicate charges</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-red-600">2.4 No Refunds After 3 Days:</strong> Once the 3-day window has passed, no refunds will be issued under any circumstances. This policy is strictly enforced.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">3. How to Request a Refund</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To request a refund within the 3-day window:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-3 mb-4">
              <li>
                <strong>Contact Support:</strong> Email us at support@automateseo.ai with subject line "Refund Request"
              </li>
              <li>
                <strong>Provide Details:</strong> Include your account email, payment date, and reason for refund
              </li>
              <li>
                <strong>Verification:</strong> We will verify that your request is within the 3-day window
              </li>
              <li>
                <strong>Processing:</strong> Approved refunds are processed within 5-7 business days
              </li>
            </ol>
            <p className="text-gray-700 leading-relaxed">
              <strong>Important:</strong> Refund requests submitted after the 3-day period will be automatically rejected.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Subscription Cancellation</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>4.1 Cancel Anytime:</strong> You can cancel your subscription at any time through your account settings or by contacting support.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>4.2 No Refund on Cancellation:</strong> Canceling your subscription after the 3-day refund period does NOT entitle you to a refund. You will continue to have access until the end of your current billing period.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>4.3 Reactivation:</strong> Canceled subscriptions can be reactivated at any time by subscribing to a plan again.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Non-Refundable Items</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following are NOT eligible for refunds under any circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Subscriptions older than 3 days from payment date</li>
              <li>Partial month refunds</li>
              <li>Add-on services purchased separately</li>
              <li>Custom development or consulting fees</li>
              <li>Third-party service costs (API usage, etc.)</li>
              <li>Usage during the trial period (no charge anyway)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Refund Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.1 Processing Time:</strong> Approved refunds are processed within 5-7 business days of approval.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.2 Refund Method:</strong> Refunds are issued to the original payment method used for the purchase.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.3 Bank Processing:</strong> Please allow an additional 5-10 business days for the refund to appear in your account, depending on your bank or payment provider.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>6.4 Notification:</strong> You will receive an email confirmation once the refund has been processed.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Account Access After Refund</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>7.1 Immediate Suspension:</strong> Once a refund is processed, your paid subscription will be immediately terminated.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>7.2 Data Retention:</strong> Your account data will be retained for 30 days after refund in case you decide to resubscribe.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>7.3 Re-subscription:</strong> You may re-subscribe at any time, but the 3-day refund policy will apply to the new subscription.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Special Circumstances</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>8.1 Service Outages:</strong> In case of extended service outages (more than 24 hours), we may offer pro-rated credits or refunds at our discretion.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>8.2 Force Majeure:</strong> Refunds may not be available for issues caused by events beyond our control (natural disasters, internet outages, etc.).
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>8.3 Abuse:</strong> Accounts found to be abusing the refund policy may be permanently banned without refund.
            </p>
          </section>

          <section className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Important Reminders</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-3">
              <li>
                <strong>3-Day Free Trial:</strong> Use the trial period to fully evaluate the service before subscribing
              </li>
              <li>
                <strong>3-Day Refund Window:</strong> Refunds are ONLY available within 3 days from the date of payment
              </li>
              <li>
                <strong>No Refunds After 3 Days:</strong> This policy is strictly enforced without exceptions
              </li>
              <li>
                <strong>Plan Your Trial:</strong> Make the most of your free trial to ensure the service meets your needs
              </li>
              <li>
                <strong>Contact Support:</strong> If you have questions, contact us before subscribing
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For refund requests or questions about this policy:
            </p>
            <ul className="list-none space-y-2 text-gray-700">
              <li><strong>Email:</strong> support@automateseo.ai</li>
              <li><strong>Subject Line:</strong> "Refund Request" (for refunds)</li>
              <li><strong>Response Time:</strong> Within 24 hours</li>
              <li><strong>Include:</strong> Account email, payment date, reason for refund</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Policy Changes</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify this refund policy at any time. Changes will be effective immediately upon posting. Your continued use of the service after changes constitutes acceptance of the modified policy.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Last updated: December 7, 2025
            </p>
          </section>
        </div>
      </div>

      <footer className="bg-gray-900 text-white py-12 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400">© 2025 AutomateSEO.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default RefundPolicy;
